import type { ComponentChildren } from "preact";
import type { Snapshot } from "./api";
import { buckets, since, volumeCost } from "./format";
import { byLabel, one, sum } from "./metrics";
import { Tooltip } from "./ui";

/**
 * 개요 카드.
 *
 * 규칙 하나: **없는 데이터를 그리지 않는다.** 지표 히스토리를 저장하지 않으므로 "지난 6시간
 * RPS 추이" 같은 건 만들 수 없고, 대신 조회로 알 수 있는 사실로 같은 질문에 답한다 —
 * 생성 시각 히스토그램(살아있는 것 기준), 누적 카운터(회수 사유·요청 상태·풀 적중), 지금의
 * 게이지(상태별 수·최고령). 그럴듯한 곡선을 채우면 그건 데이터가 아니라 장식이다.
 */

function Card({
  title,
  hint,
  tip,
  children,
}: {
  title: string;
  /** 제목 옆 짧은 라벨. 수치의 단위나 기간처럼 **한눈에 필요한 것**만 둔다. */
  hint?: string;
  /** 왜 보는 수치인지. 길어지는 설명은 전부 이쪽으로 (Tooltip 주석 참고). */
  tip?: string;
  children: ComponentChildren;
}) {
  return (
    <section class="card2">
      <h2>
        {title}
        {tip ? <Tooltip text={tip} /> : null}
        {hint ? <em class="hint">{hint}</em> : null}
      </h2>
      <div class="cbody">{children}</div>
    </section>
  );
}

/** 값이 없거나 0 이면 회색. 강조색은 "볼 게 있다"는 신호로만 쓴다. */
const Big = ({ v, label }: { v: string; label: string }) => (
  <div class="big">
    <b class={v === "0" || v === "-" ? "zero" : undefined}>{v}</b>
    <span>{label}</span>
  </div>
);

const Chips = ({ items }: { items: Array<{ key: string; value: number }> }) =>
  items.length === 0 ? (
    <span class="faint">없음</span>
  ) : (
    <span class="chips">
      {items.map((it) => (
        <span key={it.key} class="chip">
          {it.key}
          <b>{it.value}</b>
        </span>
      ))}
    </span>
  );

/**
 * 막대 히스토그램. SVG 속성(x/y/width/height)만 쓰고 CSS 를 안 쓴다 — CSP 가
 * `style-src 'self'` 라 마크업의 style 속성이 차단되고, 색은 클래스로 준다.
 */
function Bars({ data, hours }: { data: number[]; hours: number }) {
  const w = 100;
  const h = 34;
  const max = Math.max(1, ...data);
  const bw = w / data.length;
  return (
    <svg class="bars" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      {data.map((v, i) => {
        const bh = (v / max) * h;
        return (
          <rect
            key={i}
            x={i * bw + bw * 0.15}
            y={h - bh}
            width={bw * 0.7}
            height={Math.max(v > 0 ? 0.8 : 0, bh)}
            rx={0.6}
          />
        );
      })}
      <line x1="0" y1={h} x2={w} y2={h} class="axis" />
      <title>{`최근 ${hours}시간`}</title>
    </svg>
  );
}

export function Overview({ snap, onOpen }: { snap: Snapshot; onOpen: (view: string) => void }) {
  const HOURS = 6;
  const created = snap.sandboxes.map((s) => s.createdAt);
  const recentCount = created.filter((t) => Date.now() - t <= HOURS * 3_600_000).length;
  const recent = [...snap.sandboxes].sort((a, b) => b.createdAt - a.createdAt).slice(0, 3);


  const attempts = sum(snap.metrics, "tsbx_claim_total", (l) => l["outcome"] === "attempt");
  const hits = sum(snap.metrics, "tsbx_claim_total", (l) => l["outcome"] === "hit");
  const hitRate = attempts > 0 ? Math.round((hits / attempts) * 100) : null;

  const requests = sum(snap.metrics, "tsbx_requests_total");
  const failed = sum(snap.metrics, "tsbx_requests_total", (l) => {
    const s = Number(l["status"] ?? 0);
    return s >= 400;
  });
  const failedRoutes = byLabel(
    snap.metrics.filter((m) => Number(m.labels["status"] ?? 0) >= 400),
    "tsbx_requests_total",
    "route",
  ).slice(0, 3);

  const reaped = byLabel(snap.metrics, "tsbx_reaped_total", "reason").filter((r) => r.value > 0);
  const oldest = one(snap.metrics, "tsbx_oldest_sandbox_age_seconds");
  const expiring = snap.keys.filter((k) => k.expired || k.expiresAt === null).length;

  return (
    <div class="cards">
      <Card title="샌드박스" hint={`최근 ${HOURS}시간 ${recentCount}개 생성`}>
        <div class="brow">
          <Big v={snap.fleet.total} label="전체" />
          <Bars data={buckets(created, HOURS, 24)} hours={HOURS} />
        </div>
        <Chips items={snap.fleet.byState.map((s) => ({ key: s.state, value: s.value }))} />
        <ul class="mini">
          {recent.map((s) => (
            <li key={s.id}>
              <button class="link" onClick={() => onOpen("sandboxes")}>
                {s.id}
              </button>
              <span class="faint">{since(s.createdAt)} 전</span>
            </li>
          ))}
          {recent.length === 0 ? <li class="faint">없음</li> : null}
        </ul>
      </Card>

      <Card
        title="예비 풀"
        hint="claim 이 웜 파드를 잡은 비율"
        tip="적중률이 낮으면 사용자가 매번 새 파드가 뜨기를 기다립니다. 풀 target 을 올리거나, 요청 스펙(cpu·memory·disk)이 풀 스펙과 맞는지 확인하세요."
      >
        <div class="brow">
          <Big v={hitRate === null ? "-" : `${hitRate}%`} label="적중률" />
          <div class="stack">
            <span>
              <span class="faint">claim</span> {attempts}
            </span>
            <span>
              <span class="faint">hit</span> {hits}
            </span>
          </div>
        </div>
        <Chips items={byLabel(snap.metrics, "tsbx_created_total", "source")} />
      </Card>


      <Card
        title="API 오류"
        hint="컨트롤러 재시작 후 누적"
        tip="4xx·5xx 응답 수입니다. 401 이 늘면 폐기된 키를 계속 쓰는 소비자가 있다는 뜻이고, 5xx 는 우리 쪽 문제입니다. 카운터는 프로세스 메모리라 컨트롤러가 재시작하면 0 부터 다시 셉니다."
      >
        <div class="brow">
          <Big v={String(failed)} label="4xx·5xx" />
          <div class="stack">
            <span>
              <span class="faint">전체</span> {requests}
            </span>
            <span class="faint">
              {requests > 0 ? `${((failed / requests) * 100).toFixed(1)}%` : "-"}
            </span>
          </div>
        </div>
        <Chips items={failedRoutes} />
      </Card>

      <Card
        title="회수"
        hint="리퍼가 지운 누적"
        tip="무엇이 샌드박스를 지웠는지 보여줍니다. idle 은 유휴 시간 초과, aged 는 최대 수명 초과, failed 는 파드가 죽은 것, stuck 은 뜨다 만 것입니다."
      >
        <div class="brow">
          <Big v={oldest > 0 ? `${Math.floor(oldest / 3600)}h` : "-"} label="최고령 샌드박스" />
        </div>
        <Chips items={reaped} />
      </Card>

      <Card title="워크스페이스 · 키">
        <div class="brow">
          <Big v={String(snap.workspaces.length)} label="워크스페이스" />
          <Big v={String(snap.keys.length)} label="admin 키" />
        </div>
        {expiring > 0 ? (
          <p class="note2 warn">
            만료됨·무기한 키 {expiring}개
            <Tooltip
              text="만료가 없는 키는 폐기 시점을 사람이 기억해야 합니다. 키 탭에서 확인하고, 쓰지 않는 키는 폐기해 주세요."
            />
          </p>
        ) : (
          <p class="note2">모든 admin 키에 만료가 걸려 있습니다.</p>
        )}
      </Card>
    </div>
  );
}
